
import { useState } from 'react';

export default function CTA() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    phone: '',
    projectType: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.message.length > 500) return;

    setSubmitting(true);

    const urlEncodedData = new URLSearchParams();
    urlEncodedData.append('name', formData.name);
    urlEncodedData.append('email', formData.email);
    urlEncodedData.append('organization', formData.organization);
    urlEncodedData.append('phone', formData.phone);
    urlEncodedData.append('projectType', formData.projectType);
    urlEncodedData.append('message', formData.message);

    try {
      const response = await fetch(
        'https://readdy.ai/api/form/d62shopov5qgbon31n10',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: urlEncodedData.toString(),
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Network response was not ok: ${errorText}`);
      }

      setSubmitted(true);
      setFormData({
        name: '',
        email: '',
        organization: '',
        phone: '',
        projectType: '',
        message: '',
      });
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative bg-gradient-to-b from-stone-200 to-stone-100 py-24 px-12 overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-96 h-96 bg-teal-500 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-cyan-500 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto">
        {/* Vision Statement */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-12 h-1 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-full"></div>
            <span className="text-teal-600 text-sm font-bold tracking-wide uppercase">
              Partner With Us
            </span>
            <div className="w-12 h-1 bg-gradient-to-r from-cyan-500 to-teal-500 rounded-full"></div>
          </div>
          <h2 className="text-stone-800 font-black text-5xl leading-tight mb-6">
            Infrastructure. Intelligence.
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-500 to-cyan-500">
              Industrial Power.
            </span>
          </h2>
          <p className="text-stone-600 text-lg leading-relaxed max-w-3xl mx-auto">
            SkyForge Industrial Gases Inc. is building the next era of industrial gas production by combining infrastructure deployment, AI logistics intelligence, and global trade alignment. From U.S. logistics corridors to international port systems, we are positioned to become a foundational supplier powering industry, healthcare, aerospace, and energy sectors worldwide.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-16">
          {/* Left - Contact Info */}
          <div>
            <h3 className="text-stone-800 font-black text-3xl mb-6">
              Infrastructure Inquiries
            </h3>

            <p className="text-stone-600 text-base leading-relaxed mb-10">
              Explore partnership opportunities in ASU deployment, smart logistics integration, and industrial gas infrastructure development across global trade corridors.
            </p>

            {/* Contact Cards */}
            <div className="space-y-5 mb-10">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg">
                  <i className="ri-phone-line text-xl text-white"></i>
                </div>
                <div>
                  <p className="text-stone-500 text-xs uppercase tracking-wider">
                    Direct Line
                  </p>
                  <p className="text-stone-800 font-semibold text-base">
                    614-301-4667
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg">
                  <i className="ri-mail-line text-xl text-white"></i>
                </div>
                <div>
                  <p className="text-stone-500 text-xs uppercase tracking-wider">
                    Email
                  </p>
                  <p className="text-stone-800 font-semibold text-base">
                    info@skyforgegases.com
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg">
                  <i className="ri-map-pin-line text-xl text-white"></i>
                </div>
                <div>
                  <p className="text-stone-500 text-xs uppercase tracking-wider">
                    Headquarters
                  </p>
                  <p className="text-stone-800 font-semibold text-base">
                    P.O. Box 91133<br />Bexley, OH 43209
                  </p>
                </div>
              </div>
            </div>

            {/* Infrastructure Focus Areas */}
            <div className="bg-gradient-to-br from-stone-50 to-teal-50 border border-teal-200 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <i className="ri-building-line text-teal-500 text-xl"></i>
                <span className="text-stone-800 font-semibold text-base">
                  Infrastructure Focus Areas
                </span>
              </div>
              <ul className="space-y-2.5 text-sm text-stone-600">
                <li className="flex items-center gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-500"></i>
                  ASU Deployment & Operations
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-500"></i>
                  Smart Port & Logistics Integration
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-500"></i>
                  Cryogenic Distribution Networks
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-500"></i>
                  Industrial & Healthcare Supply
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-checkbox-circle-fill text-teal-500"></i>
                  Aerospace & Energy Partnerships
                </li>
              </ul>
            </div>
          </div>

          {/* Right - Form */}
          <div className="bg-gradient-to-br from-stone-50 to-stone-100 border border-stone-200 rounded-3xl p-8 shadow-xl">
            {submitted ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-16">
                <div className="w-20 h-20 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-full flex items-center justify-center mb-6 shadow-lg">
                  <i className="ri-check-line text-4xl text-white"></i>
                </div>
                <h3 className="text-stone-800 font-bold text-2xl mb-3">
                  Thank You for Your Interest
                </h3>
                <p className="text-stone-600 text-base">
                  Our infrastructure development team will contact you within 24 hours to discuss partnership opportunities.
                </p>
              </div>
            ) : (
              <form
                id="contact-form"
                data-readdy-form="true"
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                <div>
                  <h3 className="text-stone-800 font-bold text-2xl mb-2">
                    Partner with SkyForge
                  </h3>
                  <p className="text-stone-600 text-sm mb-6">
                    Submit your infrastructure project inquiry
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-5">
                  <div>
                    <label className="text-stone-600 text-xs uppercase tracking-wider mb-1.5 block font-medium">
                      Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      className="w-full bg-stone-50 border border-stone-300 rounded-xl px-4 py-3 text-stone-800 text-sm focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-100 transition-all"
                      placeholder="Full Name"
                    />
                  </div>
                  <div>
                    <label className="text-stone-600 text-xs uppercase tracking-wider mb-1.5 block font-medium">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={(e) =>
                        setFormData({ ...formData, email: e.target.value })
                      }
                      className="w-full bg-stone-50 border border-stone-300 rounded-xl px-4 py-3 text-stone-800 text-sm focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-100 transition-all"
                      placeholder="email@organization.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-5">
                  <div>
                    <label className="text-stone-600 text-xs uppercase tracking-wider mb-1.5 block font-medium">
                      Organization *
                    </label>
                    <input
                      type="text"
                      name="organization"
                      required
                      value={formData.organization}
                      onChange={(e) =>
                        setFormData({ ...formData, organization: e.target.value })
                      }
                      className="w-full bg-stone-50 border border-stone-300 rounded-xl px-4 py-3 text-stone-800 text-sm focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-100 transition-all"
                      placeholder="Organization Name"
                    />
                  </div>
                  <div>
                    <label className="text-stone-600 text-xs uppercase tracking-wider mb-1.5 block font-medium">
                      Phone
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={(e) =>
                        setFormData({ ...formData, phone: e.target.value })
                      }
                      className="w-full bg-stone-50 border border-stone-300 rounded-xl px-4 py-3 text-stone-800 text-sm focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-100 transition-all"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-stone-600 text-xs uppercase tracking-wider mb-1.5 block font-medium">
                    Project Type *
                  </label>
                  <select
                    name="projectType"
                    required
                    value={formData.projectType}
                    onChange={(e) =>
                      setFormData({ ...formData, projectType: e.target.value })
                    }
                    className="w-full bg-stone-50 border border-stone-300 rounded-xl px-4 py-3 text-stone-800 text-sm focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-100 transition-all cursor-pointer"
                  >
                    <option value="">Select Infrastructure Interest</option>
                    <option value="ASU Deployment">ASU Deployment & Operations</option>
                    <option value="Smart Port Integration">Smart Port & Logistics Integration</option>
                    <option value="Cryogenic Distribution">Cryogenic Distribution Networks</option>
                    <option value="Industrial Supply">Industrial Gas Supply Partnership</option>
                    <option value="Healthcare Supply">Healthcare & Medical Gas Supply</option>
                    <option value="Aerospace Partnership">Aerospace & Defense Partnership</option>
                    <option value="Energy Sector">Energy Sector Collaboration</option>
                    <option value="Other">Other Infrastructure Inquiry</option>
                  </select>
                </div>

                <div>
                  <label className="text-stone-600 text-xs uppercase tracking-wider mb-1.5 block font-medium">
                    Project Details *
                  </label>
                  <textarea
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    value={formData.message}
                    onChange={(e) =>
                      setFormData({ ...formData, message: e.target.value })
                    }
                    className="w-full bg-stone-50 border border-stone-300 rounded-xl px-4 py-3 text-stone-800 text-sm focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-100 transition-all resize-none"
                    placeholder="Describe your infrastructure project requirements, timeline, and partnership interests..."
                  ></textarea>
                  <p className="text-stone-400 text-xs mt-1">
                    {formData.message.length}/500
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white py-4 rounded-xl font-semibold text-sm transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 shadow-lg shadow-teal-500/25"
                >
                  {submitting ? 'Submitting...' : 'Submit Infrastructure Inquiry'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
